import React, { useState, useEffect, useRef } from 'react';

const mono = '"JetBrains Mono", monospace';

/* Car colors matching real yard inventory */
const CAR_COLORS = ['#1E293B', '#3B82F6', '#EF4444', '#F8FAFC', '#94A3B8', '#22C55E', '#6B7280', '#1E293B', '#F8FAFC', '#3B82F6', '#EF4444', '#94A3B8'];

function YardAnimation() {
  const [tick, setTick] = useState(0);
  const frameRef = useRef();

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => { t++; setTick(t); }, 200);
    return () => clearInterval(interval);
  }, []);

  /* Generate a stable grid of car blocks for each section */
  const carGrid = (rows, cols, seed) => {
    const cars = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = (seed + r * cols + c) % CAR_COLORS.length;
        /* Occasionally make a slot empty (car was just loaded/moved) */
        const fadeSlot = ((seed + r * 7 + c * 13 + Math.floor(tick / 15)) % 40) === 0;
        cars.push({ r, c, color: CAR_COLORS[idx], fade: fadeSlot });
      }
    }
    return cars;
  };

  /* Truck position along the road (loops every ~50 ticks = 10s) */
  const truckPos1 = ((tick * 2) % 120) - 10; /* truck 1: right to left */
  const truckPos2 = 110 - ((tick * 2 + 60) % 120); /* truck 2: left to right */
  const truck1Opacity = truckPos1 > 0 && truckPos1 < 100 ? 1 : 0;
  const truck2Opacity = truckPos2 > 0 && truckPos2 < 100 ? 1 : 0;

  return (
    <div style={{ width: '100%', maxWidth: 520, margin: '0 auto' }}>
      <svg viewBox="0 0 500 400" style={{ width: '100%', display: 'block' }}>
        <defs>
          <filter id="ys"><feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.08"/></filter>
        </defs>

        {/* Ground */}
        <rect width="500" height="400" rx="16" fill="#F1F5F9" />

        {/* ═══ TOP ROW: SEDANS + SUV ═══ */}
        {/* Sedans section */}
        <g transform="translate(16, 16)">
          <rect width="220" height="150" rx="10" fill="#fff" stroke="#E2E8F0" strokeWidth="1" filter="url(#ys)" />
          <text x="12" y="18" fill="#94A3B8" fontSize="7" fontFamily={mono} letterSpacing="0.1em">CARS · SEDANS</text>
          {carGrid(4, 7, 0).map((car, i) => (
            <rect key={i} x={12 + car.c * 29} y={28 + car.r * 28} width={24} height={22} rx={3}
              fill={car.color} opacity={car.fade ? 0.15 : 0.85}
              style={{ transition: 'opacity 0.8s ease' }} />
          ))}
        </g>

        {/* SUV section */}
        <g transform="translate(264, 16)">
          <rect width="220" height="150" rx="10" fill="#fff" stroke="#E2E8F0" strokeWidth="1" filter="url(#ys)" />
          <text x="12" y="18" fill="#94A3B8" fontSize="7" fontFamily={mono} letterSpacing="0.1em">CARS · SUV</text>
          {carGrid(4, 7, 42).map((car, i) => (
            <rect key={i} x={12 + car.c * 29} y={28 + car.r * 28} width={24} height={22} rx={3}
              fill={car.color} opacity={car.fade ? 0.15 : 0.85}
              style={{ transition: 'opacity 0.8s ease' }} />
          ))}
        </g>

        {/* ═══ ROAD + HQ ═══ */}
        <rect x="0" y="180" width="500" height="40" fill="#E2E8F0" rx="0" />
        {/* Road lines */}
        <line x1="30" y1="200" x2="470" y2="200" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="12 8" />

        {/* HQ entrance marker */}
        <circle cx="250" cy="200" r="6" fill="#EF4444">
          <animate attributeName="r" values="5;8;5" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="250" cy="200" r="10" fill="none" stroke="#EF4444" strokeWidth="0.8" opacity="0.3">
          <animate attributeName="r" values="8;16;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
        <text x="250" y="215" fill="#EF4444" fontSize="7" fontFamily={mono} textAnchor="middle" letterSpacing="0.08em" fontWeight="600" opacity="0.7">
          HQ · MAIN ENTRANCE
        </text>

        {/* Truck 1 (right to left — exiting with cars) */}
        <g transform={`translate(${truckPos1 * 4.5 + 20}, 188)`} opacity={truck1Opacity} style={{ transition: 'opacity 0.3s' }}>
          {/* Container body */}
          <rect width="36" height="14" rx="2" fill="#3B82F6" opacity="0.8" />
          <rect x="36" width="10" height="14" rx="2" fill="#1E40AF" opacity="0.6" />
          {/* Wheels */}
          <circle cx="8" cy="16" r="3" fill="#475569" />
          <circle cx="30" cy="16" r="3" fill="#475569" />
          <circle cx="42" cy="16" r="3" fill="#475569" />
        </g>

        {/* Truck 2 (left to right — arriving) */}
        <g transform={`translate(${truckPos2 * 4.5 + 20}, 192)`} opacity={truck2Opacity} style={{ transition: 'opacity 0.3s' }}>
          <rect width="32" height="12" rx="2" fill="#F59E0B" opacity="0.7" />
          <rect x="-8" width="8" height="12" rx="2" fill="#D97706" opacity="0.5" />
          <circle cx="6" cy="14" r="2.5" fill="#475569" />
          <circle cx="24" cy="14" r="2.5" fill="#475569" />
        </g>

        {/* ═══ BOTTOM ROW: PARTS + EXPORT READY ═══ */}
        {/* Parts warehouse */}
        <g transform="translate(16, 232)">
          <rect width="220" height="140" rx="10" fill="#fff" stroke="#E2E8F0" strokeWidth="1" filter="url(#ys)" />
          <text x="12" y="18" fill="#94A3B8" fontSize="7" fontFamily={mono} letterSpacing="0.1em">PARTS WAREHOUSE</text>
          {/* Shelving units instead of cars */}
          {[0, 1, 2, 3].map(r => (
            <g key={r}>
              {[0, 1, 2, 3, 4].map(c => (
                <rect key={c} x={12 + c * 40} y={28 + r * 26} width={35} height={20} rx={2}
                  fill={c % 2 === 0 ? '#F8FAFC' : '#E2E8F0'} stroke="#CBD5E1" strokeWidth="0.5" />
              ))}
            </g>
          ))}
        </g>

        {/* Export ready */}
        <g transform="translate(264, 232)">
          <rect width="220" height="140" rx="10" fill="#fff" stroke="#E2E8F0" strokeWidth="1" filter="url(#ys)" />
          <text x="12" y="18" fill="#94A3B8" fontSize="7" fontFamily={mono} letterSpacing="0.1em">CARS · EXPORT READY</text>
          {carGrid(4, 7, 84).map((car, i) => {
            /* Export-ready cars occasionally "leave" (fade out more often) */
            const exportFade = ((84 + car.r * 7 + car.c * 3 + Math.floor(tick / 8)) % 20) === 0;
            return (
              <rect key={i} x={12 + car.c * 29} y={28 + car.r * 28} width={24} height={22} rx={3}
                fill={car.color} opacity={exportFade ? 0.1 : 0.85}
                style={{ transition: 'opacity 1.2s ease' }} />
            );
          })}
        </g>

        {/* Compass */}
        <g transform={`translate(472, 30)`}>
          <circle r="14" fill="rgba(255,255,255,0.8)" stroke="#CBD5E1" strokeWidth="0.5" />
          <text y="-3" fill="#475569" fontSize="8" fontFamily={mono} textAnchor="middle" fontWeight="700">N</text>
          <line x1="0" y1="-10" x2="0" y2="-5" stroke="#EF4444" strokeWidth="1.5" />
          <line x1="0" y1="5" x2="0" y2="10" stroke="#CBD5E1" strokeWidth="1" />
          <animateTransform attributeName="transform" type="rotate" values="-2 472 30;2 472 30;-2 472 30" dur="6s" repeatCount="indefinite" additive="sum" />
        </g>

        {/* Scale bar */}
        <g transform="translate(16, 386)">
          <line x1="0" y1="0" x2="60" y2="0" stroke="#94A3B8" strokeWidth="1" />
          <line x1="0" y1="-3" x2="0" y2="3" stroke="#94A3B8" strokeWidth="1" />
          <line x1="60" y1="-3" x2="60" y2="3" stroke="#94A3B8" strokeWidth="1" />
          <text x="30" y="-5" fill="#94A3B8" fontSize="6" fontFamily={mono} textAnchor="middle">~50m</text>
        </g>

        {/* Coordinates label */}
        <text x="484" y="392" fill="#CBD5E1" fontSize="6" fontFamily={mono} textAnchor="end" letterSpacing="0.05em">
          25.3287°N 55.4095°E
        </text>
      </svg>
    </div>
  );
}

export default YardAnimation;
